import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import messagingService from "@/services/messagingService.js";
import {
  playUrgentAlertTone,
  primeUrgentAlertTone,
  showBrowserDeviceAlert,
  triggerUrgentHaptic,
} from "@/utils/deviceAlerts.js";
import {
  NOTIFICATION_PREFS_CHANGED_EVENT,
  readNotificationPrefsCache,
  resolveDeviceAlertPrefs,
} from "@/utils/notificationPreferences.js";

export const ACTIVE_MESSAGE_THREAD_EVENT = "afrimed:messages:thread-active";

function isFulfillmentConversation(conversation = {}) {
  const description = String(conversation?.description || "").trim().toLowerCase();
  return description.startsWith("prescription_fulfillment:");
}

function truncatePreview(content, fallback = "Open AfriMed™ Messages to view the latest message.") {
  const text = String(content || "").trim() || fallback;
  if (text.length <= 120) return text;
  return `${text.slice(0, 117).trimEnd()}...`;
}

function resolveRouteState(conversation = {}, conversationId = "") {
  const participants = Array.isArray(conversation?.conversation_participants)
    ? conversation.conversation_participants
    : [];
  const otherParticipant = participants.find(
    (participant) => String(participant?.user_id || "").trim() !== String(conversation?.currentUserId || "").trim()
  );

  return {
    conversationTitle: String(conversation?.title || "").trim(),
    participantUserId: String(otherParticipant?.user_id || "").trim() || null,
  };
}

function normalizeActiveConversationId(pathname = "") {
  const match = String(pathname || "").match(/^\/messages\/([^/?#]+)/i);
  return String(match?.[1] || "").trim();
}

export default function useMessageAlerts({ userId, role } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const stableUserId = String(userId || "").trim();
  const stableRole = String(role || "").trim().toLowerCase();
  const [alert, setAlert] = React.useState(null);
  const deliveredMessageIdsRef = React.useRef(new Set());
  const activeConversationIdRef = React.useRef(
    normalizeActiveConversationId(location.pathname)
  );
  const interactionAtRef = React.useRef(0);
  const lastSoundAtRef = React.useRef(0);
  const lastVibrationAtRef = React.useRef(0);
  const prefsRef = React.useRef(
    resolveDeviceAlertPrefs(readNotificationPrefsCache(stableRole, stableUserId), stableRole)
  );

  React.useEffect(() => {
    const markInteraction = () => {
      interactionAtRef.current = Date.now();
      void primeUrgentAlertTone();
    };

    window.addEventListener("pointerdown", markInteraction, { passive: true });
    window.addEventListener("touchstart", markInteraction, { passive: true });
    window.addEventListener("keydown", markInteraction);

    return () => {
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("touchstart", markInteraction);
      window.removeEventListener("keydown", markInteraction);
    };
  }, []);

  React.useEffect(() => {
    activeConversationIdRef.current = normalizeActiveConversationId(location.pathname);
  }, [location.pathname]);

  React.useEffect(() => {
    prefsRef.current = resolveDeviceAlertPrefs(
      readNotificationPrefsCache(stableRole, stableUserId),
      stableRole
    );

    const refreshPrefs = () => {
      prefsRef.current = resolveDeviceAlertPrefs(
        readNotificationPrefsCache(stableRole, stableUserId),
        stableRole
      );
    };

    window.addEventListener("storage", refreshPrefs);
    window.addEventListener(NOTIFICATION_PREFS_CHANGED_EVENT, refreshPrefs);

    return () => {
      window.removeEventListener("storage", refreshPrefs);
      window.removeEventListener(NOTIFICATION_PREFS_CHANGED_EVENT, refreshPrefs);
    };
  }, [stableRole, stableUserId]);

  React.useEffect(() => {
    const handleActiveThread = (event) => {
      const nextConversationId = String(
        event?.detail?.conversationId || ""
      ).trim();
      activeConversationIdRef.current = nextConversationId;
    };

    window.addEventListener(ACTIVE_MESSAGE_THREAD_EVENT, handleActiveThread);
    return () => {
      window.removeEventListener(ACTIVE_MESSAGE_THREAD_EVENT, handleActiveThread);
    };
  }, []);

  React.useEffect(() => {
    if (!stableUserId) {
      setAlert(null);
      return undefined;
    }

    let alive = true;

    const dismissAlert = () => {
      if (!alive) return;
      setAlert(null);
    };

    const handleConversationUpdate = async (payload) => {
      if (!alive || payload?.eventType !== "INSERT") return;
      if (payload?.table !== "messages") return;

      const message = payload?.new || null;
      const messageId = String(message?.id || "").trim();
      const conversationId = String(message?.conversation_id || "").trim();
      const senderId = String(message?.sender_id || "").trim();

      if (!messageId || !conversationId) return;
      if (senderId && senderId === stableUserId) return;
      if (deliveredMessageIdsRef.current.has(messageId)) return;
      deliveredMessageIdsRef.current.add(messageId);

      try {
        const conversation = await messagingService.getConversationById(conversationId);
        if (!alive || !conversation?.id) return;

        const participants = Array.isArray(conversation?.conversation_participants)
          ? conversation.conversation_participants
          : [];
        const isParticipant = participants.some(
          (participant) => String(participant?.user_id || "").trim() === stableUserId
        );
        if (!isParticipant) return;
        if (stableRole === "doctor" && isFulfillmentConversation(conversation)) return;

        const title =
          String(conversation?.title || "").trim() ||
          String(conversation?.recipient?.title || "").trim() ||
          "New message";
        const body = truncatePreview(message?.content);
        const routeState = resolveRouteState(
          {
            ...conversation,
            currentUserId: stableUserId,
          },
          conversationId
        );
        const route = `/messages/${conversationId}`;
        const appVisible = document.visibilityState === "visible";
        const isActiveConversation =
          appVisible &&
          String(activeConversationIdRef.current || "").trim() === conversationId;

        if (!appVisible) {
          const prefs = prefsRef.current || {};
          if (prefs.browserPushOn) {
            showBrowserDeviceAlert({
              title,
              body,
              tag: `message:${conversationId}`,
              route,
              onNavigate: () => {
                navigate(route, {
                  state: routeState,
                });
              },
            });
          }
          return;
        }

        const prefs = prefsRef.current || {};

        if (!prefs.inAppOn) return;

        // An open thread already displays the message, so it does not need a
        // second visual toast. Its foreground sound/haptic cue still follows
        // the recipient's selected Alerts & Sounds preference.
        if (!isActiveConversation) {
          setAlert({
            id: messageId,
            title,
            body,
            route,
            routeState,
          });
        }

        const recentInteractionMs = Date.now() - interactionAtRef.current;

        if (prefs.soundOn && recentInteractionMs <= 5 * 60_000) {
          const now = Date.now();
          if (now - lastSoundAtRef.current >= 1500) {
            const played = await playUrgentAlertTone(
              prefs.messageAlertSound
            );
            if (played) {
              lastSoundAtRef.current = now;
            }
          }
        }

        if (prefs.vibrationOn) {
          const now = Date.now();
          if (now - lastVibrationAtRef.current >= 1500) {
            triggerUrgentHaptic();
            lastVibrationAtRef.current = now;
          }
        }
      } catch (error) {
        console.warn("Message alert delivery failed:", error);
      }
    };

    const channel = messagingService.subscribeToConversations(
      stableUserId,
      handleConversationUpdate
    );

    return () => {
      alive = false;
      messagingService.unsubscribe(channel);
    };
  }, [navigate, stableRole, stableUserId]);

  return {
    alert,
    dismissAlert: () => setAlert(null),
  };
}
